import { TextFieldWithCursorAtEnd } from './TextFieldWithCursorAtEnd';
import { observer } from 'mobx-react-lite';
import { ITextField, ITextFieldProps } from '@fluentui/react/lib/TextField';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import * as React from 'react';

const styles = require('./EditableTitle.scss');
const classNames = require('classnames/bind').bind(styles);

let shouldUpdateIsEditingOnFocus = true;

export interface IEditableTitle extends ITextField {
    isTextSelected: () => boolean;
    isCursorAtStart: () => boolean;
    isCursorAtEnd: () => boolean;
}

export interface EditableTitleProps extends React.AllHTMLAttributes<HTMLElement> {
    /** When isEditing is true, the component will autofocus. Changing the value to false externally will cause the focus to be lost */
    isEditing: boolean;
    title: string;
    onEnterEdit: () => void;
    onExitEdit: () => void;
    onSaveTitle: (title: string) => void;
    onTextFieldKeyDown?: (target: React.KeyboardEvent<EventTarget>) => void;
    moveFocusAway?: () => void;
    titleStyle?: string;
    textFieldProps?: ITextFieldProps;
    titleWrapperStyle?: string;
}

export default observer(
    function EditableTitle(props: EditableTitleProps, ref: React.RefObject<IEditableTitle>) {
        const {
            isEditing,
            title,
            onEnterEdit,
            onExitEdit,
            onSaveTitle,
            onTextFieldKeyDown,
            moveFocusAway,
            placeholder,
            titleStyle,
            textFieldProps,
            titleWrapperStyle,
        } = props;

        const editTitleTextFieldRef = React.useRef<ITextField | null>(null);
        const [ignoreSaveOnBlur, setIgnoreSaveOnBlur] = React.useState<boolean>(false);

        const isTextSelected = React.useCallback((): boolean => {
            if (!isEditing) {
                return false;
            }
            const textField = editTitleTextFieldRef.current;
            return textField && textField.selectionStart !== textField.selectionEnd;
        }, [isEditing]);

        const isCursorAtStart = React.useCallback((): boolean => {
            if (!isEditing) {
                return false;
            }
            const textField = editTitleTextFieldRef.current;
            return textField && textField.selectionStart === 0;
        }, [isEditing]);

        const isCursorAtEnd = React.useCallback((): boolean => {
            if (!isEditing) {
                return false;
            }
            const textField = editTitleTextFieldRef.current;
            return textField && textField.selectionStart === textField.value.length;
        }, [isEditing]);

        React.useImperativeHandle(ref, () => ({
            isTextSelected,
            isCursorAtStart,
            isCursorAtEnd,
            ...editTitleTextFieldRef.current,
        }));

        const handleOnFocus = React.useCallback(() => {
            if (shouldUpdateIsEditingOnFocus) {
                onEnterEdit();
            } else {
                shouldUpdateIsEditingOnFocus = true;
            }
        }, [onEnterEdit]);

        const handleOnMouseDown = React.useCallback((target: React.MouseEvent<EventTarget>) => {
            shouldUpdateIsEditingOnFocus = false;
        }, []);

        const handleOnBlur = React.useCallback(() => {
            // Always exit edit mode
            onExitEdit();

            if (editTitleTextFieldRef.current) {
                if (!ignoreSaveOnBlur) {
                    onSaveTitle(editTitleTextFieldRef.current.value);
                } else {
                    setIgnoreSaveOnBlur(false);
                }
            }
        }, [onExitEdit, onSaveTitle, editTitleTextFieldRef, ignoreSaveOnBlur]);

        const handleEnter = React.useCallback(() => {
            setIgnoreSaveOnBlur(true);
            onSaveTitle(editTitleTextFieldRef.current.value);
            onExitEdit();
            moveFocusAway?.();
        }, [ignoreSaveOnBlur, onSaveTitle, editTitleTextFieldRef, onExitEdit, moveFocusAway]);

        const handleEscape = React.useCallback(() => {
            setIgnoreSaveOnBlur(true);
            // Don't save title
            onExitEdit();
            moveFocusAway?.();
        }, [ignoreSaveOnBlur, onExitEdit, moveFocusAway]);

        const onSubjectKeyDown = React.useCallback(
            (target: React.KeyboardEvent<EventTarget>) => {
                onTextFieldKeyDown?.(target);

                switch (target.keyCode) {
                    case KeyCodes.escape:
                        target.stopPropagation();
                        target.preventDefault();
                        handleEscape();
                        break;
                    case KeyCodes.enter:
                        target.stopPropagation();
                        target.preventDefault();
                        handleEnter();
                        break;
                }
            },
            [onTextFieldKeyDown, handleEscape, handleEnter]
        );

        return (
            <div className={classNames(styles.titleWrapper, titleWrapperStyle)}>
                {!isEditing ? (
                    <span
                        data-is-focusable={true}
                        tabIndex={-1}
                        className={classNames(styles.displayTitle, titleStyle)}
                        /** setting isEditing onFocus allows us to enter edit mode when the component is navigated to.
                         * We do not want this behaivor onMouseDown so we set shouldUpdateIsEditingOnFocus to false. */
                        onFocus={handleOnFocus}
                        onMouseDown={handleOnMouseDown}>
                        {title ? title : ''}
                    </span>
                ) : (
                    <TextFieldWithCursorAtEnd
                        ref={editTitleTextFieldRef}
                        className={classNames(styles.editableTitle, titleStyle)}
                        defaultValue={title}
                        placeholder={placeholder}
                        multiline={true}
                        autoFocus={true}
                        autoAdjustHeight={true}
                        resizable={false}
                        borderless={true}
                        underlined={false}
                        rows={1}
                        onKeyDown={onSubjectKeyDown}
                        aria-multiline={false}
                        onBlur={handleOnBlur}
                        {...textFieldProps}
                    />
                )}
            </div>
        );
    },
    { forwardRef: true }
);
